/**
 * Solicitudes de eliminación: el cajón del documento (contabilización, XML,
 * vinculado) y el documento completo. Specs 2026-09-24-solicitud-eliminacion.
 * Las decisiones viven en lib/deletion_request.js; aquí solo se escribe.
 *
 * Índice de pendientes: `deletion_request_pending/{pendingKeyFor(itemPath)}`
 * existe mientras haya una solicitud pendiente para ese adjunto. Se crea junto
 * con la solicitud (la regla falla si ya existe: nadie pide lo mismo dos
 * veces) y se borra al salir de `pending`. Lo lee cualquiera, así que todas
 * ven que ya está pedido.
 *
 * approve() es la pieza delicada:
 *  1. Transacción pending → approved y borrado del índice (si dos supervisoras
 *     aprueban a la vez, solo una pasa; la otra recibe "Otra supervisora ya
 *     aprobó…").
 *  2. Comprueba que lo pedido siga ahí y se pueda borrar.
 *  3. Ejecuta el borrado de siempre, que deja la entrada en la papelera.
 *  4. Anota executedAt; si falló de verdad, devuelve la solicitud a pending
 *     (con su índice) y con el error, para que se vea y se pueda reintentar.
 */

import {
  buildDeletionRequest,
  approvalDecision,
  isStillAttached,
  classifyExecutionError,
  needsExecution,
  friendlyError,
  pendingKeyFor,
  documentBlock,
} from '../../lib/deletion_request';
import { canonicalUserName } from '../../lib/user_name';
import { files as filesActions } from './documents';

const GONE_NOTE = 'Lo que se pidió eliminar ya no estaba al aprobar; no había nada que borrar.';

const col = (firebase) => firebase().firestore().collection('deletion_requests');
const pendingRef = (firebase, itemPath) =>
  firebase().firestore().collection('deletion_request_pending').doc(pendingKeyFor(itemPath));
const now = (firebase) => firebase().firestore.FieldValue.serverTimestamp();

// Nombre del perfil (users/{uid}, "Nombre completo"), no el de la cuenta de
// acceso, que suele ser solo el correo.
const currentUserName = async (firebase) => {
  const user = firebase().auth().currentUser;
  let profile;
  try {
    const snap = await firebase().firestore().doc(`users/${user?.uid}`).get();
    profile = snap.exists ? snap.data() : undefined;
  } catch (e) {
    profile = undefined;
  }
  return canonicalUserName(profile, user);
};

const pendingIndexData = (req, requestId) => ({
  requestId,
  itemPath: req.itemPath,
  parentPath: req.parentPath,
  requestedBy: req.requestedBy,
});

// Pasa pending → approved/rejected dentro de una transacción, y borra el índice.
const resolveInTx = async (firebase, id, patch) => {
  const db = firebase().firestore();
  const ref = col(firebase).doc(id);
  let data = null;
  await db.runTransaction(async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists ? snap.data() : null;
    const decision = approvalDecision(current);
    if (!decision.ok) throw new Error(decision.message);
    tx.update(ref, patch);
    tx.delete(pendingRef(firebase, current.itemPath));
    data = current;
  });
  return { ref, data };
};

// Lanza un error "sin código" (los propios se muestran tal cual, ver friendlyError).
const fail = (message) => { throw new Error(message); };

/**
 * Ejecuta el borrado de una solicitud YA aprobada. Todo va dentro del try:
 * si algo falla después de la transacción (leer, borrar o anotar executedAt),
 * la solicitud vuelve a `pending` con su índice y un error legible.
 */
const executeApproved = async ({ dispatch, firebase, ref, req, roles }) => {
  const db = firebase().firestore();
  const itemRef = db.doc(req.itemPath);
  const parentRef = db.doc(req.parentPath);
  // Quién pidió el borrado: la entrada de papelera lo guarda para que el
  // solicitante la vea (el borrado figura a nombre de la supervisora).
  const requestedBy = { uid: req.requestedBy, name: req.requestedByName || null };
  const markGone = async () => {
    await ref.update({ executedAt: now(firebase), executionError: GONE_NOTE });
    return { outcome: 'gone' };
  };

  try {
    if (req.kind === 'document') {
      const itemSnap = await itemRef.get();
      if (!itemSnap.exists) return await markGone();
      const blocked = documentBlock(itemSnap.data());
      if (blocked) fail(blocked);
      await dispatch(filesActions.delete({ roles, reference: itemRef, requestedBy }));
    } else {
      const parentSnap = await parentRef.get();
      if (!parentSnap.exists || !isStillAttached({ kind: req.kind, parentData: parentSnap.data(), itemPath: req.itemPath })) {
        return await markGone();
      }
      if (req.kind === 'accounting') {
        await dispatch(filesActions.accountingThrowBack({ roles, reference: parentRef, accountingRef: itemRef, requestedBy }));
      } else if (req.kind === 'xml') {
        await dispatch(filesActions.deleteXML({ roles, reference: parentRef, expectedPath: req.itemPath, requestedBy }));
      } else {
        await dispatch(filesActions.deleteLink({ roles, reference: parentRef, link: itemRef, requestedBy }));
      }
    }
    await ref.update({ executedAt: now(firebase), executionError: null });
    return { outcome: 'done' };
  } catch (e) {
    if (classifyExecutionError(e) === 'gone') return markGone();
    const message = friendlyError(e, 'No se pudo eliminar.');
    const batch = db.batch();
    batch.update(ref, {
      status: 'pending',
      resolvedBy: null,
      resolvedByName: null,
      resolvedAt: null,
      executionError: message,
    });
    // Vuelve a pendiente: el índice también, para que nadie la pida de nuevo.
    batch.set(pendingRef(firebase, req.itemPath), pendingIndexData(req, ref.id));
    await batch.commit().catch(() => {});
    throw new Error(message);
  }
};

export const deletionRequests = {

  create: (params) => async (dispatch, state, firebase) => {
    const user = firebase().auth().currentUser;
    const data = buildDeletionRequest({
      ...params,
      uid: user?.uid,
      userName: await currentUserName(firebase),
    });
    const idx = pendingRef(firebase, data.itemPath);
    if ((await idx.get()).exists) fail('Ya hay una solicitud pendiente para esto.');
    const ref = col(firebase).doc();
    const batch = firebase().firestore().batch();
    batch.set(ref, { ...data, requestedAt: now(firebase) });
    batch.set(idx, pendingIndexData(data, ref.id));
    await batch.commit();
    return ref.id;
  },

  cancel: ({ id }) => async (dispatch, state, firebase) => {
    const ref = col(firebase).doc(id);
    const snap = await ref.get();
    const req = snap.exists ? snap.data() : null;
    if (!req || req.status !== 'pending') fail('Esta solicitud ya fue atendida.');
    const batch = firebase().firestore().batch();
    batch.update(ref, { status: 'cancelled' });
    batch.delete(pendingRef(firebase, req.itemPath));
    await batch.commit();
  },

  reject: ({ id, rejectReason }) => async (dispatch, state, firebase) => {
    const user = firebase().auth().currentUser;
    await resolveInTx(firebase, id, {
      status: 'rejected',
      resolvedBy: user.uid,
      resolvedByName: await currentUserName(firebase),
      resolvedAt: now(firebase),
      rejectReason: String(rejectReason || '').trim() || null,
    });
  },

  approve: ({ id, roles }) => async (dispatch, state, firebase) => {
    const user = firebase().auth().currentUser;
    const { ref, data: req } = await resolveInTx(firebase, id, {
      status: 'approved',
      resolvedBy: user.uid,
      resolvedByName: await currentUserName(firebase),
      resolvedAt: now(firebase),
    });
    return executeApproved({ dispatch, firebase, ref, req, roles });
  },

  /**
   * Reintenta el borrado de una aprobada que no alcanzó a ejecutarse (falló
   * después de la transacción o se cerró la pestaña). Ver needsExecution.
   */
  retry: ({ id, roles }) => async (dispatch, state, firebase) => {
    const ref = col(firebase).doc(id);
    const snap = await ref.get();
    const req = snap.exists ? snap.data() : null;
    if (!needsExecution(req)) fail('Esta solicitud ya no necesita ejecutarse.');
    return executeApproved({ dispatch, firebase, ref, req, roles });
  },
};
